import React, { useCallback, useEffect, useRef, useState } from 'react'

import { LeftArrowThinIcon, RightArrowThinIcon } from 'images'
import FacebookLinkComponent from './FacebookLinkComponent'
import InstagramLinkComponent from './InstagramLinkComponent'
import ReelsLinkCarousel from './ReelsLinkCarousel'

const CarouselPreview = (props) => {
  const {cards, mobileView = false, onLoaded, type = 'facebook', callToAction} = props
  const carouselRef = useRef(null)

  const [selectedInstagramCard, setSelectedInstagramCard] = useState(0)
  const [selectedReelsCard, setSelectedReelsCard] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  if (!Array.isArray(cards) || !cards.length) return null

  const updateScrollState = useCallback(() => {
    if (type !== 'facebook' || !carouselRef.current) return
    const {scrollLeft, scrollWidth, clientWidth} = carouselRef.current
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    const threshold = 1

    setCanScrollLeft(scrollLeft > threshold)
    setCanScrollRight(scrollLeft < maxScrollLeft - threshold)
  }, [type])

  useEffect(() => {
    if (type !== 'facebook') return

    const frameId = window.requestAnimationFrame(updateScrollState)
    return () => window.cancelAnimationFrame(frameId)
  }, [cards, mobileView, type, updateScrollState])

  const handleScroll = (direction) => {
    if (!carouselRef.current || type !== 'facebook') return
    const scrollOffset = mobileView ? 280 : 480
    const {scrollLeft, scrollWidth, clientWidth} = carouselRef.current
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    const nextScrollLeft = direction === 'left'
      ? Math.max(0, scrollLeft - scrollOffset)
      : Math.min(maxScrollLeft, scrollLeft + scrollOffset)

    if (Math.abs(nextScrollLeft - scrollLeft) < 1) return

    carouselRef.current.scrollTo({
      left: nextScrollLeft,
      behavior: 'smooth',
    })
  }

  switch (type) {
    case 'facebook':
      return (
        <div className="carousel-preview-wrapper">
          <button
            type="button"
            className="button-scroll button-scroll-left"
            onClick={() => handleScroll('left')}
            disabled={!canScrollLeft}
          >
            <LeftArrowThinIcon width={8} />
          </button>
          <div className="carousel-preview" ref={carouselRef} onScroll={updateScrollState}>
            {cards.map((card, index) => (
              <FacebookLinkComponent
                key={card?.id || `${index}-${card?.url || card?.media?.url || 'facebook-card'}`}
                card={card}
                mobileView={mobileView}
                onLoaded={onLoaded}
                callToAction={callToAction}
              />
            ))}
          </div>
          <button
            type="button"
            className="button-scroll button-scroll-right"
            onClick={() => handleScroll('right')}
            disabled={!canScrollRight}
          >
            <RightArrowThinIcon width={8} height={8}/>
          </button>
        </div>
      )
    case 'instagram':
      return (
        <div className="carousel-preview-wrapper">
          {selectedInstagramCard > 0 && <button type="button" className="button-scroll button-scroll-left" onClick={() => setSelectedInstagramCard(prev => prev - 1)}>
            <LeftArrowThinIcon width={8} />
          </button>}
          <div className="instagram-carousel-preview" ref={carouselRef}>
            <InstagramLinkComponent
              card={cards[selectedInstagramCard]}
              onLoaded={onLoaded}
              callToAction={callToAction}
              type={type}
            />
          </div>
          {cards.length > 1 && selectedInstagramCard < cards.length - 1 && <button type="button" className="button-scroll button-scroll-right" onClick={() => setSelectedInstagramCard(prev => prev + 1)}>
            <RightArrowThinIcon width={8} height={8}/>
          </button>}
          
          <div className="instagram-carousel-dots">
            {cards.map((_, index) => {
              return (
                <div key={index} className={`instagram-carousel-dot ${selectedInstagramCard === index ? 'active' : ''}`} />
              )
            })}
          </div>
        </div>
      )
    case 'reels':
      return (
        <div className="carousel-preview-wrapper">
          {selectedReelsCard > 0 && (
            <button
              type="button"
              className="button-scroll button-scroll-left"
              onClick={() => setSelectedReelsCard((prev) => prev - 1)}
            >
              <LeftArrowThinIcon width={8} />
            </button>
          )}
          <div className="reels-carousel-preview" ref={carouselRef}>
            <ReelsLinkCarousel card={cards[selectedReelsCard]} onLoaded={onLoaded} />
          </div>
          {cards.length > 1 && selectedReelsCard < cards.length - 1 && (
            <button
              type="button"
              className="button-scroll button-scroll-right"
              onClick={() => setSelectedReelsCard((prev) => prev + 1)}
            >
              <RightArrowThinIcon width={8} height={8} />
            </button>
          )}
        </div>
      )
    default:
      return null
  }
}

export default CarouselPreview
